/* ============================================================
   SAM-198 — live preview rail for the full-screen service editor.

   Renders what the form will look like to a guest in the app and to the
   front desk while it is being edited, straight from `useServiceEditor`'s
   formData (no API call): name, the booking options as the price lines,
   add-ons with their required flag, and a front-desk total for one booking
   of the first option plus any required add-ons. A unit-priced service
   (SAM-591) leads with the unit price and its included guests; with rate
   groups (SAM-588) a "Preview as" picker shows one group's prices, the way
   the app only ever shows the guest their own tariff. Presentation only — the
   real price is still computed server-side from booking_options
   (functions/booking/pricing.py); this is a visual aid, never a quote.
   ============================================================ */
import React, { useState } from 'react';
import type { FormData } from '../../hooks/useServiceEditor';
import { resolveUnitConfig } from './unitPricing';
import { badgeOption, isServiceBadge, suggestedBadge } from '../../utils/serviceBadge';
import { capitalize, resourceWord } from './editorV2';
import { formatMoneyAmount } from '../../utils/money';

/** Same formatter as the public pay page (/pay/:token), so both read "RD$ 1,000". */
const money = (amount: number, currency: string): string => formatMoneyAmount(amount, currency);

function num(v: string | undefined): number {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

type Surface = 'app' | 'desk';

/** `setupLabel` is accepted for callers but not drawn: the app shows the badge, not the setup. */
export function ServiceLivePreview({ formData }: { formData: FormData; setupLabel?: string }) {
  // Same badge the app draws over the photo: the saved one, else the setup's suggestion.
  const badgeKey = isServiceBadge(formData.badge) ? formData.badge : suggestedBadge(formData.subtype);
  const badge = badgeOption(badgeKey);
  const BadgeIcon = badge.icon;
  const unitName = formData.resources[0]?.name || capitalize(resourceWord(formData.subtype).singular);
  const [surface, setSurface] = useState<Surface>('app');
  const name = formData.name_es || formData.name_en || 'Nombre del servicio';
  const options = formData.booking_options.filter((o) => o.label_es || o.label_en || o.price);
  const addOns = formData.add_ons.filter((a) => a.label_es || a.label_en);
  const first = options[0];
  const currency = first?.currency || addOns[0]?.currency || 'DOP';
  const requiredTotal = addOns.filter((a) => a.required).reduce((t, a) => t + num(a.price), 0);

  const unit = formData.price_unit === 'unit' ? resolveUnitConfig(formData, formData.resources[0]) : null;
  const groups = formData.price_unit === 'person'
    ? formData.rate_groups.filter((g) => g.id.trim()) : [];
  const [groupPick, setGroupPick] = useState<string>('');
  const group = groups.find((g) => g.id.trim() === groupPick)
    || groups.find((g) => g.is_default) || groups[0];
  const groupLabel = (g: typeof group) => (g ? g.label_es || g.label_en || g.id : '');
  // The price a line shows: the unit's extra-guest charge, the previewed group's cell (an empty
  // cell = not sold to that group), else the flat price.
  const linePrice = (o: typeof options[number]): number | null => {
    if (unit) return num(o.extra_guest_price);
    const gid = group?.id.trim();
    const cell = gid ? o.prices_by_group?.[gid] : undefined;
    if (gid && Object.keys(o.prices_by_group || {}).some((k) => String(o.prices_by_group[k]).trim() !== '')) {
      return cell != null && String(cell).trim() !== '' ? num(cell) : null;
    }
    return num(o.price);
  };
  const deskTotal = (unit ? unit.price : (first ? linePrice(first) ?? 0 : 0)) + requiredTotal;

  const tab = (key: Surface, label: string) => (
    <button
      type="button"
      onClick={() => setSurface(key)}
      aria-pressed={surface === key}
      className="text-[12px] font-semibold px-3 py-1.5"
      style={{
        border: 'none',
        borderRadius: 'var(--r-8)',
        background: surface === key ? '#fff' : 'transparent',
        color: surface === key ? 'var(--ink-900)' : 'var(--ink-500)',
        boxShadow: surface === key ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex p-0.5" role="group" aria-label="Preview surface" style={{ background: 'var(--line-subtle)', borderRadius: 'var(--r-8)' }}>
        {tab('app', 'App')}
        {tab('desk', 'Front desk')}
      </div>

      {surface === 'app' ? (
        <div style={{ width: 280, background: '#111827', borderRadius: 36, padding: 8 }}>
          <div className="bg-white flex flex-col" style={{ borderRadius: 29, overflow: 'hidden', minHeight: 520 }}>
            <div className="relative" style={{ height: 150, background: formData.background_color || 'var(--tenant-primary, #005488)' }}>
              <span data-testid="preview-badge" className="absolute inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 bg-white" style={{ top: 12, left: 12, borderRadius: 999, color: 'var(--ink-900)' }}>
                <BadgeIcon size={12} /> {badge.label}
              </span>
            </div>
            <div className="p-4 flex flex-col gap-2">
              <div className="text-[18px] font-bold leading-tight" style={{ color: 'var(--ink-900)' }}>{name}</div>
              {unit && (
                <div className="flex justify-between gap-3 text-[13px]" style={{ color: 'var(--ink-700)' }}>
                  <span>
                    {unitName}
                    {unit.included_pax > 0 && ` · ${unit.included_pax} personas incluidas`}
                  </span>
                  <span className="font-semibold whitespace-nowrap">{money(unit.price, first?.currency || currency)}</span>
                </div>
              )}
              {groups.length > 0 && (
                <div className="text-[11px] font-semibold px-2 py-0.5 self-start" style={{ borderRadius: 999, background: 'var(--line-subtle)', color: 'var(--ink-700)' }}>
                  Tarifa {groupLabel(group)}
                </div>
              )}
              {!unit && options.length === 0 ? (
                <div className="text-[13px]" style={{ color: 'var(--ink-500)' }}>Gratis</div>
              ) : options.map((o) => {
                const price = linePrice(o);
                if (price == null) return null;
                // A line whose price is still empty draws nothing (not "US$ 0"): QA 21 oct, New service step 4.
                if (!unit && String(o.price ?? '').trim() === '' && price === 0) return null;
                return (
                  <div key={o.id} className="flex justify-between gap-3 text-[13px]" style={{ color: 'var(--ink-700)' }}>
                    <span>{o.label_es || o.label_en}</span>
                    <span className="font-semibold whitespace-nowrap">{unit ? '+ ' : ''}{money(price, o.currency)}</span>
                  </div>
                );
              })}
              {addOns.length > 0 && (
                <div className="mt-1 pt-2" style={{ borderTop: '1px solid var(--line-subtle)' }}>
                  <div className="text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: 'var(--ink-400)' }}>Extras</div>
                  {addOns.map((a) => (
                    <div key={a.id || a.label_en} className="flex justify-between text-[12px] py-0.5" style={{ color: 'var(--ink-700)' }}>
                      <span>
                        {a.label_es || a.label_en}
                        {a.required && <span className="ml-1 text-[10px]" style={{ color: 'var(--warn-fg)' }}>incluido</span>}
                      </span>
                      <span>+ {money(num(a.price), a.currency)}</span>
                    </div>
                  ))}
                </div>
              )}
              <span
                className="mt-3 flex items-center justify-center text-[14px] font-bold text-white"
                style={{ height: 42, borderRadius: 'var(--r-12)', background: 'var(--tenant-primary, #005488)' }}
              >
                Reservar
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full bg-white p-4 flex flex-col gap-2" style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-12)' }}>
          <div className="text-[14px] font-semibold" style={{ color: 'var(--ink-900)' }}>{formData.name_en || name}</div>
          {unit ? (
            <div className="flex justify-between text-[13px]"><span>{unitName} × 1 ({unit.included_pax} incl.)</span><span>{money(unit.price, first?.currency || currency)}</span></div>
          ) : first ? (
            <div className="flex justify-between text-[13px]"><span>{first.label_en || first.label_es} × 1{group ? ` · ${group.label_en || group.id}` : ''}</span><span>{money(linePrice(first) ?? 0, first.currency)}</span></div>
          ) : (
            <div className="text-[13px]" style={{ color: 'var(--ink-500)' }}>Free to book</div>
          )}
          {addOns.map((a) => (
            <div key={a.id || a.label_en} className="flex justify-between text-[13px]" style={{ color: 'var(--ink-700)' }}>
              <span>{a.label_en || a.label_es} × {a.required ? 1 : 0}</span>
              <span>{money(a.required ? num(a.price) : 0, a.currency)}</span>
            </div>
          ))}
          <div className="flex justify-between text-[14px] font-bold pt-2" style={{ borderTop: '1px solid var(--line)' }}>
            <span>Total</span><span>{money(deskTotal, currency)}</span>
          </div>
        </div>
      )}
      {groups.length > 1 && (
        <label className="text-[11px] flex items-center gap-2" style={{ color: 'var(--ink-500)' }}>
          Preview as
          <select
            value={group?.id.trim() || ''}
            onChange={(e) => setGroupPick(e.target.value)}
            className="rounded px-1.5 py-1 text-[11px]"
            style={{ border: '1px solid var(--line)' }}
          >
            {groups.map((g) => (
              <option key={g.id} value={g.id.trim()}>{g.label_en || g.label_es || g.id}</option>
            ))}
          </select>
        </label>
      )}
      <p className="text-[11px] text-center" style={{ color: 'var(--ink-400)' }}>
        Preview only. The price is always calculated when the guest books.
      </p>
    </div>
  );
}
